/**
 * A route on a map, stop to stop, with the numbered list under it.
 *
 * Moved out of the crew day screen (pages/crew/CrewDayTimer) so the owner's
 * Field page shows the same map (Sep 24 2026: "add a map with stop order, so I
 * can see the route"). Numbers follow the order of `stops`; stops with no GPS
 * keep their number in the list and are simply not pinned.
 */
import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { placeLabel } from '../utils/format';
import { workLine, WorkJob } from '../utils/crewWork';
import WorkLine from './crew/WorkLine';

/**
 * Where the day starts (owner, Oct 26 2026: "add a start location pin on the
 * routes map for the crew"): the route's own start address when the office set
 * one, else home base — the server decides (GET /route-sessions/active `start`,
 * services/crewReorder.startPoint). Drawn first; the route line begins there.
 */
export type StartPoint = { lat: number; lng: number; label: string; source: 'home' | 'route' };

/**
 * The crew's reorder (owner, Oct 26 2026: "allow them to change the order of
 * the route"): big up/down buttons on the stops still to do — thumb-sized, no
 * tiny handles, so they work on a phone with gloves. The screen owning the
 * list keeps the draft; the board only draws it and reports a tap.
 */
export type ReorderControls = {
  canMove: (s: RouteStop) => boolean;
  onMove: (id: number, dir: -1 | 1) => void;
};

/**
 * The fields the map and list read. The crew screen's Stop is a superset.
 * The work fields (WorkJob) say what to do there: "Mowing", with anything
 * else on the visit as Additional (utils/crewWork).
 */
export type RouteStop = WorkJob & {
  id: number; status: string;
  customer_name: string | null;
  address: string | null;
  property_label: string | null;
  lat: number | null; lng: number | null;
};

// Name is the big line, address the small one (owner, Sep 8).
export function stopLabel(s: RouteStop | null | undefined): string {
  if (!s) return 'Next stop';
  return s.customer_name
    || placeLabel(s.property_label)
    || (s.address ? s.address.split(',')[0] : null)
    || 'Next stop';
}
/** Whether the stop says what it is (a map pin from a quote visit does not). */
const knowsWork = (s: RouteStop) => !!(s.work_main || s.service_type);

export function stopAddress(s: RouteStop | null | undefined): string {
  if (!s) return '';
  const label = placeLabel(s.property_label);
  return [label, s.address ? s.address.split(',')[0] : null].filter(Boolean).join(' · ');
}

// ── Map pieces ────────────────────────────────────────────────────────────────

const PIN_CSS = `
@keyframes crew-pin-pulse { 0% { box-shadow: 0 0 0 0 rgba(37,99,235,.45); } 70% { box-shadow: 0 0 0 14px rgba(37,99,235,0); } 100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); } }
.crew-pin { display:flex; align-items:center; justify-content:center; border-radius:9999px; color:#fff; font-weight:700; font-size:12px; border:2px solid #fff; width:28px; height:28px; }
.crew-pin-current { animation: crew-pin-pulse 1.6s infinite; }
.crew-user-dot { width:16px; height:16px; border-radius:9999px; background:#2563eb; border:3px solid #fff; box-shadow:0 0 6px rgba(0,0,0,.4); }
.crew-pin-start { display:flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:8px; background:#15803d; color:#fff; font-size:15px; line-height:1; border:2px solid #fff; box-shadow:0 1px 4px rgba(0,0,0,.35); }
.crew-start-label { background:#15803d !important; color:#fff !important; border:0 !important; font-weight:700; font-size:11px; padding:1px 6px !important; box-shadow:none !important; }
.crew-start-label::before { border-right-color:#15803d !important; }
`;

// A flag on a green square: unlike any numbered stop, so "where the day starts" reads at a glance.
const startIcon = L.divIcon({
  className: '',
  html: '<div class="crew-pin-start">\u2691</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function stopIcon(n: number, kind: 'done' | 'current' | 'todo' | 'skipped') {
  const bg = kind === 'done' ? '#22c55e' : kind === 'current' ? '#2563eb' : kind === 'skipped' ? '#d1d5db' : '#64748b';
  const cls = kind === 'current' ? 'crew-pin crew-pin-current' : 'crew-pin';
  const label = kind === 'done' ? '✓' : String(n);
  return L.divIcon({
    className: '',
    html: `<div class="${cls}" style="background:${bg}">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const userIcon = L.divIcon({
  className: '',
  html: '<div class="crew-user-dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

/** Frame the whole route so stop 1, the line, and the last stop are all on screen. */
function FitRoute({ points }: { points: [number, number][] }) {
  const map = useMap();
  const key = points.map(p => `${p[0]},${p[1]}`).join('|');
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 15);
    else map.fitBounds(points, { padding: [28, 28] });
    // key is the point list; points itself is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export function RouteMap({ stops, currentId, gps, start = null }: {
  stops: RouteStop[];
  currentId: number | null;
  gps: { lat: number; lng: number } | null;
  start?: StartPoint | null;
}) {
  const ordered = stops.filter(s => s.lat != null && s.lng != null);
  const startPt: [number, number] | null = start && Number.isFinite(Number(start.lat)) && Number.isFinite(Number(start.lng))
    ? [Number(start.lat), Number(start.lng)] : null;
  // The route line begins at the Start pin, then stop to stop.
  const line = [...(startPt ? [startPt] : []), ...ordered.map(s => [s.lat as number, s.lng as number] as [number, number])];
  const current = ordered.find(s => s.id === currentId) || null;
  const center: [number, number] = line[0] || (gps ? [gps.lat, gps.lng] : [30.2672, -97.7431]);

  if (!ordered.length) return null;

  return (
    <div className="h-64 w-full">
    <style>{PIN_CSS}</style>
    <MapContainer center={center} zoom={12} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {line.length > 1 && (
        <Polyline positions={line} pathOptions={{ color: '#7c3aed', weight: 3, opacity: 0.85 }} />
      )}
      {startPt && (
        <Marker position={startPt} icon={startIcon} zIndexOffset={-100}>
          {/* One tooltip only — Leaflet keeps one per marker, a second replaces the first. The address is under the map. */}
          <Tooltip direction="right" permanent offset={[14, 0]} className="crew-start-label">Start</Tooltip>
        </Marker>
      )}
      {ordered.map(s => {
        const n = stops.findIndex(x => x.id === s.id) + 1;
        const kind = s.id === currentId ? 'current'
          : s.status === 'complete' ? 'done'
          : s.status === 'skipped' ? 'skipped' : 'todo';
        return (
          <Marker key={s.id} position={[s.lat as number, s.lng as number]} icon={stopIcon(n, kind)}>
            <Tooltip direction="top">
              {/* Leaflet labels never wrap on their own — this one does, so a long work line stays on a phone's map */}
              <span className="block max-w-[220px] whitespace-normal text-xs font-medium">{n}. {stopLabel(s)}{knowsWork(s) ? ` · ${workLine(s)}` : ''}</span>
            </Tooltip>
          </Marker>
        );
      })}
      {gps && <Marker position={[gps.lat, gps.lng]} icon={userIcon} />}
      {gps && current && (
        <Polyline
          positions={[[gps.lat, gps.lng], [current.lat as number, current.lng as number]]}
          pathOptions={{ color: '#2563eb', dashArray: '6 8', weight: 3, opacity: 0.7 }} />
      )}
      <FitRoute points={line} />
    </MapContainer>
    </div>
  );
}

export function RouteBoard({ stops, currentId, gps, start = null, reorder = null }: {
  stops: RouteStop[];
  currentId: number | null;
  gps: { lat: number; lng: number } | null;
  start?: StartPoint | null;
  reorder?: ReorderControls | null;
}) {
  if (!stops.length) return null;
  const pinned = stops.some(s => s.lat != null && s.lng != null);
  // While the order is being changed the list grows (big buttons) and the whole day stays reachable.
  const movable = reorder ? stops.filter(reorder.canMove) : [];
  const firstMovable = movable[0]?.id, lastMovable = movable[movable.length - 1]?.id;
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
      {pinned
        ? <RouteMap stops={stops} currentId={currentId} gps={gps} start={start} />
        : <p className="px-4 py-3 text-sm text-gray-500">These stops are not on the map yet.</p>}
      {start && <p className="px-3 py-1.5 text-xs text-green-800 bg-green-50 border-b border-green-100 font-semibold truncate">⚑ Start — {start.label}</p>}
      <ol className={`${reorder ? 'max-h-[60vh]' : 'max-h-52'} overflow-y-auto divide-y divide-gray-100`}>
        {stops.map((s, i) => {
          const done = s.status === 'complete' || s.status === 'skipped';
          const current = s.id === currentId;
          const canMove = !!reorder && reorder.canMove(s);
          return (
            <li key={s.id} className={`flex items-start gap-2.5 px-3 py-2 ${current ? 'bg-blue-50' : ''} ${reorder && canMove ? 'bg-amber-50/60' : ''}`}>
              <span className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${
                current ? 'bg-blue-600' : s.status === 'complete' ? 'bg-green-500' : s.status === 'skipped' ? 'bg-gray-300' : 'bg-gray-500'
              }`}>{s.status === 'complete' ? '✓' : i + 1}</span>
              <div className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold leading-snug ${done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{stopLabel(s)}</span>
                {knowsWork(s) && <WorkLine job={s} className={done ? 'opacity-60' : ''} />}
                {stopAddress(s) && <span className="block text-xs text-gray-500 truncate">{stopAddress(s)}</span>}
                {reorder && !canMove && !done && <span className="block text-[11px] text-gray-500">Started — stays here</span>}
              </div>
              {reorder && canMove && (
                <div className="flex flex-shrink-0 gap-1.5 -my-0.5">
                  <button type="button" aria-label={`Move ${stopLabel(s)} up`} disabled={s.id === firstMovable}
                    onClick={() => reorder.onMove(s.id, -1)}
                    className="h-11 w-11 rounded-xl bg-white border-2 border-gray-300 text-gray-800 flex items-center justify-center active:bg-brand-100 disabled:opacity-30">
                    <ChevronUp className="h-6 w-6" />
                  </button>
                  <button type="button" aria-label={`Move ${stopLabel(s)} down`} disabled={s.id === lastMovable}
                    onClick={() => reorder.onMove(s.id, 1)}
                    className="h-11 w-11 rounded-xl bg-white border-2 border-gray-300 text-gray-800 flex items-center justify-center active:bg-brand-100 disabled:opacity-30">
                    <ChevronDown className="h-6 w-6" />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
